import React, { useState, useEffect } from 'react';
import firebaseApp from '../Firecontact';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import Header from '../Header';
import Footer from '../Footer';
import pdfBundleImg from '../assets/image/pdf_bundle.png';

// Custom Interactive Before/After Image Slider
const BeforeAfterSlider = ({ before, after }) => {
  const [sliderPos, setSliderPos] = useState(50);

  return (
    <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
      {/* After Image (AI Generated) */}
      <img src={after} alt="After" style={{ width: '100%', height: 'auto', display: 'block' }} />
      <Chip label="After AI" size="small" sx={{ position: 'absolute', top: 12, right: 12, fontWeight: '500', zIndex: 3, bgcolor: '#25d366', color: '#fff' }} />

      {/* Before Image with Clip-Path */}
      <img
        src={before}
        alt="Before"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          clipPath: `polygon(0 0, ${sliderPos}% 0, ${sliderPos}% 100%, 0 100%)`,
          display: 'block'
        }}
      />
      <Chip label="Before" size="small" sx={{ position: 'absolute', top: 12, left: 12, fontWeight: '500', zIndex: 3, bgcolor: '#ffc107', color: '#131921' }} />

      {/* Vertical divider line */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: `${sliderPos}%`,
        width: '3px',
        bgcolor: '#ffffff',
        zIndex: 4,
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
        boxShadow: '0 0 10px rgba(0,0,0,0.5)'
      }}>
        {/* Handle circle */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          bgcolor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
          border: '2.5px solid #3a7bd5'
        }}>
          <span style={{ color: '#3a7bd5', fontWeight: 'bold', fontSize: '12px' }}>
            ◀▶
          </span>
        </Box>
      </Box>

      {/* Invisible Range Input spanning the container */}
      <input
        type="range"
        min="0"
        max="100"
        value={sliderPos}
        onChange={(e) => setSliderPos(e.target.value)}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          zIndex: 5,
          cursor: 'ew-resize',
          margin: 0
        }}
      />
    </Box>
  );
};

// ૫. PDF Bundle Purchase Helper
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PdfProductBox = ({ product }) => {
  const [buyerMobile, setBuyerMobile] = useState('');
  const [buyerBusiness, setBuyerBusiness] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (openModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [openModal]);

  useEffect(() => {
    const savedPurchases = JSON.parse(localStorage.getItem('purchasedPdfs') || '[]');
    if (savedPurchases.includes(product.id)) {
      setPurchased(true);
    }
    const savedMobile = localStorage.getItem('buyerMobile');
    const savedBusiness = localStorage.getItem('buyerBusiness');
    if (savedMobile) setBuyerMobile(savedMobile);
    if (savedBusiness) setBuyerBusiness(savedBusiness);
  }, [product.id]);

  const triggerDownload = () => {
    const link = document.createElement('a');
    link.href = process.env.PUBLIC_URL + (product.pdfUrl || '');
    link.download = `${product.title}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!buyerMobile || !buyerBusiness) {
      toast.error("Please fill in both mobile number and business name.");
      return;
    }

    setPurchasing(true);

    try {
      const db = firebaseApp.firestore();
      const existingPurchase = await db.collection('pdf_purchases')
        .where('mobile', '==', buyerMobile)
        .where('product', '==', product.id)
        .get();

      if (!existingPurchase.empty) {
        setPurchased(true);
        setOpenModal(false);

        const savedPurchases = JSON.parse(localStorage.getItem('purchasedPdfs') || '[]');
        localStorage.setItem('purchasedPdfs', JSON.stringify([...new Set([...savedPurchases, product.id])]));
        localStorage.setItem('buyerMobile', buyerMobile);
        localStorage.setItem('buyerBusiness', buyerBusiness);

        toast.success(`Welcome back ${buyerBusiness}! You already purchased this PDF. You can download it now.`);
        setPurchasing(false);
        triggerDownload();
        return;
      }
    } catch (error) {
      console.error("Error checking existing purchase: ", error);
    }

    const res = await loadRazorpayScript();
    if (!res) {
      toast.error("Razorpay failed to load. Check your connection.");
      setPurchasing(false);
      return;
    }

    const options = {
      key: "rzp_test_RGylGTr37qW531",
      amount: "29900", // 299.00 INR
      currency: "INR",
      name: "9R AI Studio",
      description: `${product.title} (PDF)`,
      handler: async function (response) {
        try {
          const db = firebaseApp.firestore();
          await db.collection('pdf_purchases').add({
            product: product.id,
            mobile: buyerMobile,
            businessName: buyerBusiness,
            paymentId: response.razorpay_payment_id,
            amount: 299,
            timestamp: new Date()
          });
          setPurchased(true);
          setOpenModal(false);

          const savedPurchases = JSON.parse(localStorage.getItem('purchasedPdfs') || '[]');
          localStorage.setItem('purchasedPdfs', JSON.stringify([...new Set([...savedPurchases, product.id])]));
          localStorage.setItem('buyerMobile', buyerMobile);
          localStorage.setItem('buyerBusiness', buyerBusiness);

          toast.success("Payment successful! Your PDF is downloading...");
          triggerDownload();
        } catch (error) {
          console.error("Error saving purchase: ", error);
          toast.error("Payment received but error saving details. Please contact support.");
        }
      },
      prefill: {
        contact: buyerMobile,
      },
      theme: {
        color: "#0b1121"
      }
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.on('payment.failed', function (response) {
      toast.error("Payment Failed. Please try again.");
    });
    paymentObject.open();
    setPurchasing(false);
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
      borderRadius: 4,
      overflow: 'hidden',
      height: '100%',
      position: 'relative'
    }}>
      <Box sx={{ position: 'absolute', top: '-20%', left: '-20%', width: '140%', height: '140%', background: 'radial-gradient(circle at center, rgba(232,255,0,0.03) 0%, transparent 60%)', zIndex: 0 }} />

      <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Box sx={{
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3,
          boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.05)'
        }}>
          <img src={product.img} alt={product.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
        </Box>

        <Chip label="PREMIUM AI PROMPTS" size="small" sx={{ bgcolor: 'rgba(232, 255, 0, 0.1)', color: '#e8ff00', fontWeight: 'bold', mb: 1.5, border: '1px solid rgba(232, 255, 0, 0.2)', fontSize: '0.7rem', width: 'fit-content' }} />

        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1, fontSize: '20px' }}>
          {product.title}
        </Typography>

        <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, fontSize: '14px', lineHeight: 1.5 }}>
          {product.desc}
        </Typography>

        <Box sx={{ mt: 'auto' }}>
          {!purchased ? (
            <Button
              fullWidth
              onClick={() => setOpenModal(true)}
              sx={{
                mt: 1,
                py: 1.2,
                bgcolor: '#e8ff00',
                color: '#0b1121',
                fontWeight: 700,
                fontSize: '15px',
                '&:hover': { bgcolor: '#c8eb00' },
                textTransform: 'none',
                borderRadius: 2
              }}
            >
              Buy Now - ₹299
            </Button>
          ) : (
            <Box sx={{ p: 2, bgcolor: 'rgba(37, 211, 102, 0.1)', border: '1px solid rgba(37, 211, 102, 0.3)', borderRadius: 2, textAlign: 'center' }}>
              <Typography sx={{ color: '#25d366', fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, fontSize: '14px' }}>
                <CheckIcon fontSize="small" /> Payment Successful!
              </Typography>
              <Button
                fullWidth
                href={process.env.PUBLIC_URL + product.pdfUrl}
                download={`${product.title}.pdf`}
                target="_blank"
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  bgcolor: '#25d366',
                  color: '#fff',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#1ea550' },
                  textTransform: 'none'
                }}
              >
                Download PDF
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Checkout Modal */}
      <Dialog
        open={openModal}
        onClose={() => !purchasing && setOpenModal(false)}
        disableScrollLock={false}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(11, 17, 33, 0.8)',
            backdropFilter: 'blur(12px)'
          }
        }}
        PaperProps={{
          sx: {
            bgcolor: '#131929',
            color: '#fff',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.1)',
            minWidth: { xs: '90%', sm: 400 }
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', pb: 2, fontSize: '18px', fontWeight: 600 }}>
          Checkout - {product.title}
        </DialogTitle>
        <form onSubmit={handlePurchase}>
          <DialogContent sx={{ pt: 3 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, fontSize: '14px' }}>
              Please enter your details to proceed with the payment of ₹299.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mobile Number"
                  variant="outlined"
                  required
                  value={buyerMobile}
                  onChange={(e) => setBuyerMobile(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(0,0,0,0.2)', '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' } },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)', fontSize: '14px' },
                    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#e8ff00' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e8ff00' },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #0b1121 inset !important',
                      WebkitTextFillColor: '#ffffff !important',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Business Name"
                  variant="outlined"
                  required
                  value={buyerBusiness}
                  onChange={(e) => setBuyerBusiness(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(0,0,0,0.2)', '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' } },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)', fontSize: '14px' },
                    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#e8ff00' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#e8ff00' },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #0b1121 inset !important',
                      WebkitTextFillColor: '#ffffff !important',
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <Button
              onClick={() => setOpenModal(false)}
              disabled={purchasing}
              sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={purchasing}
              sx={{
                bgcolor: '#e8ff00',
                color: '#0b1121',
                fontWeight: 600,
                '&:hover': { bgcolor: '#c8eb00' },
                textTransform: 'none'
              }}
            >
              {purchasing ? <CircularProgress size={20} sx={{ color: '#0b1121' }} /> : 'Proceed to Pay'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default function AiTrendsGallery() {
  const [trends, setTrends] = useState([]);
  const [filteredTrends, setFilteredTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [selectedTrend, setSelectedTrend] = useState(null);

  const pdfProducts = [
    { id: 'jewelry', title: 'Jewelry AI Prompts', desc: 'Unlock professional prompt formulas specifically engineered for high-end Jewelry product photography.', img: pdfBundleImg, pdfUrl: '/JEWELLERY LUXURY PROMPTS.pdf' },
    { id: 'clothing', title: 'Fashion AI Prompts', desc: 'Generate ultra-realistic fashion and apparel photoshoots on virtual models with these Fashion AI Prompts.', img: pdfBundleImg, pdfUrl: '/Fashion Product promt.pdf' },
    { id: 'cosmetics', title: 'Cosmetics AI Prompts', desc: 'Create stunning, glowing cosmetic product layouts and beauty brand imagery with these AI prompts.', img: pdfBundleImg, pdfUrl: '/Universal Luxury Cosmetic Product Prompts.pdf' },
    { id: 'fragrance', title: 'Fragrance AI Prompts', desc: 'Generate luxurious perfume bottle renders with water splashes, dramatic lighting, and premium backgrounds.', img: pdfBundleImg, pdfUrl: '/Luxury Product parfum Prompts.pdf' },
    { id: 'fmcg', title: 'FMCG AI Prompts', desc: 'Perfect prompts for fast moving consumer goods, packaging designs, and lifestyle product placements.', img: pdfBundleImg, pdfUrl: '/FMCG product promt.pdf' }
  ];

  // ૧. ફાયરબેઝ ફાયરસ્ટોરમાંથી ડેટા ઇન્ડેક્સ-ફ્રી ફેચ કરવાનો કોડ
  useEffect(() => {
    const fetchAiTrends = async () => {
      setLoading(true);
      try {
        const db = firebaseApp.firestore();

        // ડાયરેક્ટ આખી કલેક્શન ગેટ કરો (જેથી કમ્પોઝિટ ઇન્ડેક્સ એરર ન આવે)
        const snapshot = await db.collection('ai_trends').get();
        const allData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // ક્લાયન્ટ સાઇડ પર જ એક્ટિવ સ્ટેટસ ફિલ્ટર કરો અને ઓર્ડર વાઇઝ સોર્ટ કરો
        const activeTrends = allData
          .filter(item => item.status === 'active')
          .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

        setTrends(activeTrends);
        setFilteredTrends(activeTrends);
      } catch (error) {
        console.error("Error fetching AI Trends on user side: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAiTrends();
  }, []);

  // ૨. સર્ચબાર ફિલ્ટર લોજિક
  useEffect(() => {
    const filtered = trends.filter(trend =>
      trend.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (trend.category && trend.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (trend.shortDescription && trend.shortDescription.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    setFilteredTrends(filtered);
    setFilteredTrends(filtered);
  }, [searchQuery, trends]);

  // ૩. Body Scroll Lock જ્યારે મોડલ ઓપન હોય
  useEffect(() => {
    if (selectedTrend) {
      const scrollY = window.scrollY;

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";

      return () => {
        const savedScrollY = document.body.style.top;

        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        document.body.style.overflow = "";

        window.scrollTo(0, parseInt(savedScrollY || "0") * -1);
      };
    }
  }, [selectedTrend]);

  // ૪. પ્રોમ્પ્ટ કોપી કરવાનું ફંક્શન
  const handleCopyPrompt = (id, promptText) => {
    // HTML ટેગ્સ વગર પ્લેન ટેક્સ્ટ કોપી કરવા માટે
    const plainText = (promptText || '').replace(/<[^>]*>/g, '');
    navigator.clipboard.writeText(plainText);
    setCopiedId(id);
    toast.success("Prompt copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at 0% 30%, rgba(232, 255, 0, 0.12) 0%, transparent 45%), radial-gradient(circle at 100% 30%, rgba(0, 100, 255, 0.15) 0%, transparent 40%), #0b1121'
      }}>
        <Header name="AiContentStudio" />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={50} sx={{ color: '#e8ff00' }} />
        </Box>
        <Footer name="AiContentStudio" />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      fontFamily: '"Poppins", sans-serif',
      background: 'radial-gradient(circle at 0% 30%, rgba(232, 255, 0, 0.12) 0%, transparent 45%), radial-gradient(circle at 100% 30%, rgba(0, 100, 255, 0.15) 0%, transparent 40%), #0b1121'
    }}>
      <Header name="AiContentStudio" />
      <Box sx={{ pt: 8, pb: 8 }} md={{ pt: 10, pb: 12 }}>
        <div className="container">
          {/* હેડિંગ વિભાગ */}
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.05)',
              px: 3, py: 1,
              borderRadius: 50,
              border: '1px solid rgba(255,255,255,0.1)',
              mb: 2
            }}>
              <Typography sx={{ color: '#e8ff00', fontWeight: '500', fontSize: '14px', mr: 1 }}>★ ★ ★ ★ ★</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Empowering Modern Brands & Creators</Typography>
            </Box>

            <Typography variant="h1" fontWeight="800" sx={{
              color: '#ffffff',
              mb: 3,
              fontSize: { xs: '40px', md: '64px' },
              lineHeight: 1.1,
              fontFamily: '"Poppins", sans-serif'
            }}>
              Elevate Your Brand with <br />
              <span style={{ color: '#e8ff00' }}>9R AI Content Studio.</span>
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 700, mx: 'auto', fontSize: '20px', color: 'rgba(255,255,255,0.7)', fontWeight: 300, fontFamily: '"Poppins", sans-serif' }}>
              Discover expertly crafted AI prompts and tools to generate professional product photography, marketing assets, and stunning visuals for your business effortlessly.
            </Typography>
          </Box>

          {/* 6 Individual PDF Products Section */}
          <Box sx={{ mb: 6 }}>
            <Grid container spacing={4} justifyContent="center">
              {pdfProducts.map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id} sx={{ display: 'flex' }}>
                  <PdfProductBox product={product} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* સર્ચ બાર */}
          <Box sx={{ mb: 6, display: 'flex', justifyContent: 'center' }}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Search templates or categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#3a7bd5' }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 50,
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0px 3px 20px rgba(0, 0, 0, 0.2)',
                  fontFamily: '"Poppins", sans-serif',
                  '& fieldset': { border: '1px solid rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { border: '1px solid rgba(255,255,255,0.3)' },
                  '&.Mui-focused fieldset': { border: '1px solid #e8ff00' },
                  '& input': { color: '#ffffff', py: 2 }
                }
              }}
              sx={{ maxWidth: 600, input: { color: '#ffffff' } }}
            />
          </Box>

          {/* ગ્રીડ લિસ્ટ */}
          <Box>
            <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center">
            {filteredTrends.length === 0 ? (
              <Grid item xs={12} sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" sx={{ color: '#646163', fontFamily: '"Poppins", sans-serif' }}>No active AI trends found.</Typography>
              </Grid>
            ) : (
              filteredTrends.map((trend) => (
                <Grid item xs={12} sm={6} md={4} key={trend.id} sx={{ display: 'flex' }}>
                  <Card sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.5)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: 4,
                    overflow: 'hidden',
                    bgcolor: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(20px)',
                    transition: 'transform 0.4s ease, border-color 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: 'rgba(232, 255, 0, 0.3)'
                    }
                  }}>
                    {/* Before / After Image Slider */}
                    <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: '#f5f5f5', width: '100%' }}>
                      {trend.beforeImageUrl && trend.afterImageUrl ? (
                        <Box sx={{ width: '100%', height: 'auto', '& img': { width: '100%', height: 'auto', display: 'block' } }}>
                          <BeforeAfterSlider before={trend.beforeImageUrl} after={trend.afterImageUrl} />
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: 'auto', position: 'relative' }}>
                          <img src={trend.afterImageUrl || trend.beforeImageUrl} alt={trend.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
                          <Chip label={trend.afterImageUrl ? "After AI" : "Before"} size="small" sx={{ position: 'absolute', top: 12, left: 12, fontWeight: '500', bgcolor: trend.afterImageUrl ? '#25d366' : '#ffc107', color: trend.afterImageUrl ? '#fff' : '#131921' }} />
                        </Box>
                      )}
                      {/* Trending / Featured badges */}
                      <Box sx={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', gap: 1, zIndex: 10 }}>
                        {trend.isTrending && <Chip label="Trending 🔥" size="small" sx={{ fontWeight: '600', height: 22, fontSize: '0.7rem', bgcolor: '#ff4d4f', color: '#fff' }} />}
                        {trend.isFeatured && <Chip label="Pro ⭐" size="small" sx={{ fontWeight: '700', height: 22, fontSize: '0.7rem', bgcolor: '#e8ff00', color: '#0b1121' }} />}
                      </Box>
                    </Box>

                    {/* Content */}
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', px: 2, pt: 1.5, pb: '16px !important' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#e8ff00', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 1, fontFamily: '"Poppins", sans-serif', fontSize: '0.65rem' }}>
                          {trend.category}
                        </Typography>
                      </Box>

                      <Typography variant="h6" sx={{ mb: 0.5, color: '#ffffff', fontWeight: '600', fontSize: '1rem', lineClamp: 1, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: '"Poppins", sans-serif' }}>
                        {trend.title}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.6)', fontWeight: 300, fontSize: '0.75rem', flexGrow: 1, lineClamp: 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: '"Poppins", sans-serif' }}>
                        {trend.shortDescription}
                      </Typography>

                      {/* Actions */}
                      <Box sx={{ mt: 'auto' }}>
                        <Button
                          fullWidth
                          size="small"
                          startIcon={<VisibilityIcon fontSize="small" />}
                          onClick={() => setSelectedTrend(trend)}
                          sx={{
                            borderRadius: 50,
                            fontWeight: '600',
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            fontFamily: '"Poppins", sans-serif',
                            color: '#0b1121',
                            bgcolor: '#e8ff00',
                            py: 0.8,
                            '&:hover': {
                              bgcolor: '#d4e600',
                              transform: 'scale(1.02)'
                            }
                          }}
                        >
                          Get the AI Prompt →
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
            </Grid>
          </Box>

          {/* પ્રોમ્પ્ટ જોવા માટેનું મોડલ પોપઅપ */}
          <Dialog
            open={Boolean(selectedTrend)}
            onClose={() => setSelectedTrend(null)}
            maxWidth="md"
            fullWidth
            disableScrollLock={false}
            BackdropProps={{
              sx: {
                backgroundColor: 'rgba(11, 17, 33, 0.8)',
                backdropFilter: 'blur(12px)'
              }
            }}
            PaperProps={{
              sx: {
                borderRadius: { xs: 2, sm: 4 },
                m: { xs: 1, sm: 2 },
                width: { xs: 'calc(100% - 8px)', sm: 'auto' },
                boxShadow: '0 20px 50px rgba(0,0,0,0.7)',
                bgcolor: '#0b1121', // Solid dark background for modal
                border: '1px solid rgba(255,255,255,0.1)'
              }
            }}
          >
            {selectedTrend && (
              <>
                <DialogTitle sx={{ fontWeight: '600', fontSize: { xs: '1.2rem', sm: '1.6rem' }, p: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 1 }, color: '#ffffff', fontFamily: '"Poppins", sans-serif' }}>
                  {selectedTrend.title}
                  <Typography variant="body2" sx={{ color: '#e8ff00', fontWeight: '600', display: 'inline-block', ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}>
                    <Chip label={selectedTrend.category} size="small" sx={{ fontWeight: '600', bgcolor: 'rgba(232, 255, 0, 0.1)', color: '#e8ff00', border: '1px solid rgba(232,255,0,0.2)' }} />
                  </Typography>
                </DialogTitle>
                <DialogContent sx={{
                  p: { xs: 1.5, sm: 3 },
                  '&::-webkit-scrollbar': { width: '8px' },
                  '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.02)', borderRadius: '4px' },
                  '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '4px' },
                  '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(232, 255, 0, 0.5)' }
                }}>
                  <Grid container spacing={{ xs: 1.5, sm: 3 }} alignItems="flex-start">
                    {/* Image Section */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ position: 'relative', width: '100%', height: '100%', maxHeight: '70vh', borderRadius: 3, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 10px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {selectedTrend.beforeImageUrl && selectedTrend.afterImageUrl ? (
                          <Box sx={{ width: '100%', height: '100%', maxHeight: '70vh', '& img': { width: '100%', height: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' } }}>
                            <BeforeAfterSlider before={selectedTrend.beforeImageUrl} after={selectedTrend.afterImageUrl} />
                          </Box>
                        ) : (
                          <Box sx={{ width: '100%', height: '100%', maxHeight: '70vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={selectedTrend.afterImageUrl || selectedTrend.beforeImageUrl} alt={selectedTrend.title} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }} />
                          </Box>
                        )}
                      </Box>
                    </Grid>

                    {/* Content Section */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)', fontWeight: 300, fontFamily: '"Poppins", sans-serif', fontSize: '1.05rem' }}>
                        {selectedTrend.shortDescription}
                      </Typography>

                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#ffffff', fontWeight: '600', fontFamily: '"Poppins", sans-serif' }}>
                        Optimized AI Prompt:
                      </Typography>
                      <Box
                        sx={{
                          p: { xs: 1.5, sm: 2.5 },
                          bgcolor: 'rgba(0,0,0,0.3)',
                          borderRadius: 3,
                          fontFamily: 'monospace',
                          fontSize: '0.9rem',
                          color: '#e8ff00',
                          whiteSpace: 'pre-wrap',
                          border: '1px solid rgba(255,255,255,0.1)',
                          flexGrow: 1,
                          overflowY: 'auto',
                          mb: 1,
                          boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.2)',
                          '&::-webkit-scrollbar': { width: '6px' },
                          '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
                          '&::-webkit-scrollbar-thumb': { background: 'rgba(232, 255, 0, 0.3)', borderRadius: '4px' },
                          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(232, 255, 0, 0.8)' }
                        }}
                        dangerouslySetInnerHTML={{ __html: selectedTrend.fullPrompt }}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                  <Button onClick={() => setSelectedTrend(null)} variant="outlined" sx={{
                    borderRadius: 1.5,
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.6)',
                    borderColor: 'rgba(255,255,255,0.2)',
                    fontFamily: '"Poppins", sans-serif',
                    '&:hover': { borderColor: '#e8ff00', bgcolor: 'rgba(232, 255, 0, 0.05)', color: '#e8ff00' }
                  }}>
                    Close
                  </Button>
                  <Button
                    startIcon={copiedId === selectedTrend.id ? <CheckIcon /> : <ContentCopyIcon />}
                    onClick={() => handleCopyPrompt(selectedTrend.id, selectedTrend.fullPrompt)}
                    sx={{
                      borderRadius: 1.5,
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      fontFamily: '"Poppins", sans-serif',
                      px: 3,
                      bgcolor: copiedId === selectedTrend.id ? '#25d366' : '#e8ff00',
                      color: copiedId === selectedTrend.id ? 'white' : '#0b1121',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: copiedId === selectedTrend.id ? '#20b958' : '#d4e600',
                        transform: 'scale(1.02)'
                      }
                    }}
                  >
                    {copiedId === selectedTrend.id ? "Copied!" : "Copy Prompt"}
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        </div>
      </Box>
      <Footer name="AiContentStudio" />
    </Box>
  );
}
